import validateRut from './validations/validateRut';

export type CountryCurrency = 'CLP' | 'UF' | 'ARS' | 'PEN' | 'USD' | 'CRC' | 'GTQ' | 'MXN' | 'UYU' | 'COP';
export type CountryIdentityField = 'rut' | 'dni';
export type CountryPrimaryFinancialField = 'salary' | 'budget';

interface CountryIdentityConfig {
  field: CountryIdentityField;
  label: string;
  placeholder: string;
  errorMessage: string;
  validate: (document: string) => boolean;
}

interface CountryFinancialConfig {
  primaryField: CountryPrimaryFinancialField;
  primaryLabel: 'Ingreso' | 'Presupuesto';
  primaryDisplayLabel: 'Ingresos' | 'Presupuesto';
  displayCurrency: CountryCurrency;
  primaryCurrencyOptions: readonly CountryCurrency[];
  savingsCurrencyOptions: readonly CountryCurrency[];
}

interface CountryConfig {
  label: string;
  locale: string;
  phonePlaceholder: string;
  defaultClientCurrency: CountryCurrency;
  pricingCurrencies: readonly CountryCurrency[];
  propertyPriceCurrency: CountryCurrency;
  identity: CountryIdentityConfig;
  financial: CountryFinancialConfig;
}

const digitsOnly = (document: string): string => document.replace(/\D/g, '');

const validateArgentinaDocument = (document: string): boolean => /^\d{8}$/.test(document.replace(/[\s.]/g, ''));

const validatePeruDocument = (document: string): boolean => {
  const normalized = document.replace(/\s/g, '');
  return !['12345678', '87654321'].includes(normalized) && /^\d{8}$/.test(normalized);
};

const validateColombiaDocument = (document: string): boolean => /^[1-9]\d{4,9}$/.test(document.replace(/[.\-\s]/g, ''));

const validateCostaRicaDocument = (document: string): boolean => /^[1-9]\d{8}$/.test(document.replace(/[-\s]/g, ''));

const validateGuatemalaDocument = (document: string): boolean => {
  const normalized = document.replace(/[\s-]/g, '');
  if (!/^\d{13}$/.test(normalized)) return false;

  const correlative = normalized.slice(0, 8);
  const verifier = Number(normalized.charAt(8));
  const sum = [...correlative].reduce((total, digit, index) => total + Number(digit) * (index + 2), 0);
  return verifier === sum % 11;
};

const validateMexicoDocument = (document: string): boolean => {
  const normalized = document.trim().toUpperCase().replace(/[\s-]/g, '');
  const curpRegex = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/;
  const rfcRegex = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/;
  return curpRegex.test(normalized) || rfcRegex.test(normalized);
};

const validateUruguayDocument = (document: string): boolean => {
  const digits = digitsOnly(document);
  if (!/^\d{7,8}$/.test(digits)) return false;

  const factors = [2, 9, 8, 7, 6, 3, 4];
  const body = digits.slice(0, -1).padStart(7, '0');
  const checkDigit = Number(digits.slice(-1));
  const sum = factors.reduce((total, factor, index) => total + Number(body[index]) * factor, 0);
  return (10 - (sum % 10)) % 10 === checkDigit;
};

const validateElSalvadorDocument = (document: string): boolean => {
  const digits = digitsOnly(document);
  if (!/^\d{9}$/.test(digits)) return false;

  const weights = [9, 8, 7, 6, 5, 4, 3, 2];
  const body = digits.slice(0, 8);
  const checkDigit = Number(digits.slice(8));
  const sum = weights.reduce((total, weight, index) => total + Number(body[index]) * weight, 0);
  return (10 - (sum % 10)) % 10 === checkDigit;
};

export const COUNTRY_CONFIG = {
  CL: {
    label: 'Chile',
    locale: 'es-CL',
    phonePlaceholder: '+56912345678',
    defaultClientCurrency: 'CLP',
    pricingCurrencies: ['UF', 'CLP'],
    propertyPriceCurrency: 'UF',
    identity: {
      field: 'rut',
      label: 'RUT',
      placeholder: '12.345.678-9',
      errorMessage: 'RUT inválido',
      validate: validateRut,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'CLP',
      primaryCurrencyOptions: ['CLP'],
      savingsCurrencyOptions: ['CLP'],
    },
  },
  AR: {
    label: 'Argentina',
    locale: 'es-AR',
    phonePlaceholder: '+5491112345678',
    defaultClientCurrency: 'USD',
    pricingCurrencies: ['USD', 'ARS'],
    propertyPriceCurrency: 'USD',
    identity: {
      field: 'dni',
      label: 'DNI',
      placeholder: '20.123.456',
      errorMessage: 'DNI inválido (8 dígitos)',
      validate: validateArgentinaDocument,
    },
    financial: {
      primaryField: 'budget',
      primaryLabel: 'Presupuesto',
      primaryDisplayLabel: 'Presupuesto',
      displayCurrency: 'ARS',
      primaryCurrencyOptions: ['ARS', 'USD'],
      savingsCurrencyOptions: ['ARS', 'USD'],
    },
  },
  PE: {
    label: 'Perú',
    locale: 'es-PE',
    phonePlaceholder: '+51912345678',
    defaultClientCurrency: 'PEN',
    pricingCurrencies: ['PEN', 'USD'],
    propertyPriceCurrency: 'PEN',
    identity: {
      field: 'dni',
      label: 'DNI',
      placeholder: '20123456',
      errorMessage: 'DNI inválido (8 dígitos)',
      validate: validatePeruDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'PEN',
      primaryCurrencyOptions: ['PEN'],
      savingsCurrencyOptions: ['PEN'],
    },
  },
  CR: {
    label: 'Costa Rica',
    locale: 'es-CR',
    phonePlaceholder: '+50688888888',
    defaultClientCurrency: 'CRC',
    pricingCurrencies: ['CRC', 'USD'],
    propertyPriceCurrency: 'CRC',
    identity: {
      field: 'dni',
      label: 'Cédula',
      placeholder: '1-2345-6789',
      errorMessage: 'Cédula inválida',
      validate: validateCostaRicaDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'CRC',
      primaryCurrencyOptions: ['CRC', 'USD'],
      savingsCurrencyOptions: ['CRC', 'USD'],
    },
  },
  GT: {
    label: 'Guatemala',
    locale: 'es-GT',
    phonePlaceholder: '+50212345678',
    defaultClientCurrency: 'GTQ',
    pricingCurrencies: ['GTQ', 'USD'],
    propertyPriceCurrency: 'GTQ',
    identity: {
      field: 'dni',
      label: 'DPI',
      placeholder: '1234 56789 0101',
      errorMessage: 'DPI inválido',
      validate: validateGuatemalaDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'GTQ',
      primaryCurrencyOptions: ['GTQ', 'USD'],
      savingsCurrencyOptions: ['GTQ', 'USD'],
    },
  },
  MX: {
    label: 'México',
    locale: 'es-MX',
    phonePlaceholder: '+5215512345678',
    defaultClientCurrency: 'MXN',
    pricingCurrencies: ['MXN', 'USD'],
    propertyPriceCurrency: 'MXN',
    identity: {
      field: 'dni',
      label: 'CURP o RFC',
      placeholder: 'GOMJ800101HDFRRN09',
      errorMessage: 'CURP o RFC inválido',
      validate: validateMexicoDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'MXN',
      primaryCurrencyOptions: ['MXN', 'USD'],
      savingsCurrencyOptions: ['MXN', 'USD'],
    },
  },
  UY: {
    label: 'Uruguay',
    locale: 'es-UY',
    phonePlaceholder: '+59899123456',
    defaultClientCurrency: 'UYU',
    pricingCurrencies: ['UYU', 'USD'],
    propertyPriceCurrency: 'UYU',
    identity: {
      field: 'dni',
      label: 'Cédula',
      placeholder: '1.234.567-2',
      errorMessage: 'Cédula inválida',
      validate: validateUruguayDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'UYU',
      primaryCurrencyOptions: ['UYU', 'USD'],
      savingsCurrencyOptions: ['UYU', 'USD'],
    },
  },
  CO: {
    label: 'Colombia',
    locale: 'es-CO',
    phonePlaceholder: '+573161234567',
    defaultClientCurrency: 'COP',
    pricingCurrencies: ['COP', 'USD'],
    propertyPriceCurrency: 'COP',
    identity: {
      field: 'dni',
      label: 'Cédula',
      placeholder: '12.345.678',
      errorMessage: 'Cédula inválida',
      validate: validateColombiaDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'COP',
      primaryCurrencyOptions: ['COP', 'USD'],
      savingsCurrencyOptions: ['COP', 'USD'],
    },
  },
  SV: {
    label: 'El Salvador',
    locale: 'es-SV',
    phonePlaceholder: '+50370123456',
    defaultClientCurrency: 'USD',
    pricingCurrencies: ['USD'],
    propertyPriceCurrency: 'USD',
    identity: {
      field: 'dni',
      label: 'DUI',
      placeholder: '12345678-4',
      errorMessage: 'DUI inválido',
      validate: validateElSalvadorDocument,
    },
    financial: {
      primaryField: 'salary',
      primaryLabel: 'Ingreso',
      primaryDisplayLabel: 'Ingresos',
      displayCurrency: 'USD',
      primaryCurrencyOptions: ['USD'],
      savingsCurrencyOptions: ['USD'],
    },
  },
} as const satisfies Record<string, CountryConfig>;

export type CountryCode = keyof typeof COUNTRY_CONFIG;

export const COUNTRY_CODES = Object.keys(COUNTRY_CONFIG) as CountryCode[];

export const COUNTRY_OPTIONS = COUNTRY_CODES.map((code) => ({
  code,
  label: COUNTRY_CONFIG[code].label,
}));

export const COUNTRIES = Object.fromEntries(COUNTRY_OPTIONS.map(({ code, label }) => [code, label])) as Record<
  CountryCode,
  string
>;

export const DEFAULT_COUNTRY_CODE: CountryCode = 'CL';

export const isCountryCode = (value: unknown): value is CountryCode =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(COUNTRY_CONFIG, value);

export const resolveCountryCode = (countryCode?: string | null): CountryCode | undefined => {
  if (!countryCode) return DEFAULT_COUNTRY_CODE;
  const normalizedCountryCode = countryCode.toUpperCase();
  return isCountryCode(normalizedCountryCode) ? normalizedCountryCode : undefined;
};

export const resolveCountryConfig = (countryCode?: string | null): CountryConfig | undefined => {
  const resolvedCountryCode = resolveCountryCode(countryCode);
  return resolvedCountryCode ? COUNTRY_CONFIG[resolvedCountryCode] : undefined;
};

export const getCountryConfig = (countryCode?: string | null): CountryConfig =>
  resolveCountryConfig(countryCode) ?? COUNTRY_CONFIG[DEFAULT_COUNTRY_CODE];
